/**
 * The phone and tablet navigation drawer.
 *
 * Below 990px the header bar is one row — mark, search, cart — and everything
 * else moves in here. Two things are worth knowing before editing:
 *
 * - The country selector and the theme switch are *moved*, not re-rendered.
 *   `localization-form` ships the whole country list, and a second copy of it
 *   in every page's markup is not worth a row of the drawer. Comment nodes
 *   mark where each control came from, so putting them back at a wider
 *   viewport lands them in their original order however the other one moved.
 * - The panel is a real `<dialog>` opened with `showModal()`: top layer,
 *   focus trap and Escape come for free. Because it is given a `display`, the
 *   stylesheet has to hide `:not([open])` itself — the UA rule loses to any
 *   author `display`.
 */

const COMPACT = '(max-width: 989px)';
const CLOSE_MS = 320;

class AgMenu {
  #events = new AbortController();
  /** @type {AbortController | null} */
  #closeController = null;
  #closeTimer = 0;
  /** @type {{ node: HTMLElement, marker: Comment }[]} */
  #utils = [];

  constructor() {
    this.dialog = /** @type {HTMLDialogElement | null} */ (document.querySelector('dialog.ag-menu'));
    this.header = document.querySelector('.ag-header');
    if (!this.dialog || !this.header) return;

    this.panel = this.dialog.querySelector('[data-ag-menu-panel]');
    this.slot = this.dialog.querySelector('[data-ag-menu-utils]');
    this.triggers = /** @type {HTMLElement[]} */ (
      Array.from(this.header.querySelectorAll('[data-ag-menu-open]'))
    );

    const { signal } = this.#events;

    for (const trigger of this.triggers) {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(trigger);
      }, { signal });
    }

    this.dialog.addEventListener('click', (event) => {
      const target = /** @type {HTMLElement} */ (event.target);
      if (target.closest('[data-ag-menu-close]')) {
        this.close();
        return;
      }
      /* Everything visible sits inside the panel, so a click that lands on the
         dialog itself landed on the backdrop beside it. */
      if (!this.panel?.contains(target)) this.close();
    }, { signal });

    /* Escape is the dialog's own; `cancel` is where it arrives, and the close
       has to run through ours so the panel animates out and the lock lifts. */
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    }, { signal });

    /* A link to the page we are already on navigates nowhere, so nothing would
       take the drawer away. */
    this.dialog.addEventListener('click', (event) => {
      const link = /** @type {HTMLElement} */ (event.target).closest('a[href]');
      if (link) this.close();
    }, { signal });

    /* Restored from the back/forward cache with the drawer open, the page
       would be locked behind a panel nobody asked for. */
    window.addEventListener('pageshow', (event) => {
      if (event.persisted && this.dialog?.open) this.#finishClose();
    }, { signal });

    this.#adopt('.ag-header__country');
    this.#adopt('.ag-theme-btn');
    this.#place();

    this.#query = window.matchMedia(COMPACT);
    this.#query.addEventListener('change', () => {
      this.#place();
      /* The drawer is a phone control; at a desktop width there is a full menu
         bar behind it and no way left to close it. */
      if (!this.#query.matches) this.#finishClose();
    }, { signal });
  }

  /** @type {MediaQueryList | undefined} */
  #query;

  /**
   * Remember where a header control lives, so it can go back there.
   * @param {string} selector
   */
  #adopt(selector) {
    const node = /** @type {HTMLElement | null} */ (this.header?.querySelector(selector));
    if (!node || !node.parentElement) return;
    const marker = document.createComment(' ag-menu:home ');
    node.parentElement.insertBefore(marker, node);
    this.#utils.push({ node, marker });
  }

  /** Park the borrowed controls on whichever side of the breakpoint we are. */
  #place() {
    const compact = window.matchMedia(COMPACT).matches;
    for (const { node, marker } of this.#utils) {
      const target = compact ? this.slot : marker.parentElement;
      if (!target || node.parentElement === target) continue;
      /* The country panel's outside-click listener is on `document` and its
         width is measured from its old box; closing it first spares both. */
      /** @type {any} */ (node).hidePanel?.();
      if (compact) target.append(node);
      else target.insertBefore(node, marker);
    }
    this.slot?.toggleAttribute('hidden', !compact || this.#utils.length === 0);
  }

  /** True between `close()` and the end of the closing transition. */
  get #closing() {
    return this.#closeController !== null;
  }

  #cancelClose() {
    this.#closeController?.abort();
    this.#closeController = null;
    window.clearTimeout(this.#closeTimer);
  }

  /** @param {HTMLElement} [opener] */
  open(opener) {
    if (!this.dialog || (this.dialog.open && !this.#closing)) return;
    const reopening = this.#closing;
    this.#cancelClose();
    if (!reopening) this.#opener = opener ?? null;

    if (!this.dialog.open) {
      this.dialog.showModal();
      /* Opened again after a scroll to the promo at the bottom, the drawer
         would come back where it was left rather than at the menu. */
      const scroll = this.dialog.querySelector('.ag-menu__scroll');
      if (scroll) scroll.scrollTop = 0;
    }
    document.documentElement.setAttribute('scroll-lock', '');
    for (const trigger of this.triggers) trigger.setAttribute('aria-expanded', 'true');

    /* Two frames: `showModal` and the class in the same one collapse into one
       style recalculation and the panel would appear rather than slide. */
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.dialog?.classList.add('is-open'));
    });

    /** @type {HTMLElement | null} */ (this.dialog.querySelector('[data-ag-menu-close]'))?.focus();
  }

  close() {
    if (!this.dialog?.open || this.#closing) return;
    this.dialog.classList.remove('is-open');

    this.#closeController = new AbortController();
    const done = () => this.#finishClose();
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      done();
      return;
    }
    /* transitionend is the signal, the timer the guarantee — a panel that was
       never painted transitions nothing. */
    this.panel?.addEventListener('transitionend', done, { once: true, signal: this.#closeController.signal });
    this.#closeTimer = window.setTimeout(done, CLOSE_MS);
  }

  #finishClose() {
    this.#cancelClose();
    if (!this.dialog) return;
    this.dialog.classList.remove('is-open');
    if (this.dialog.open) this.dialog.close();
    for (const trigger of this.triggers) trigger.setAttribute('aria-expanded', 'false');
    this.#unlock();
    this.#opener?.focus();
    this.#opener = null;
  }

  /** @type {HTMLElement | null} */
  #opener = null;

  /**
   * `html[scroll-lock]` is Horizon's own lock and base.css is what reads it.
   * The test is the search overlay's, so the two release it the same way.
   */
  #unlock() {
    const others = document.querySelectorAll('dialog[open][scroll-lock], dialog[open] [scroll-lock]');
    if (others.length === 0) document.documentElement.removeAttribute('scroll-lock');
  }

  destroy() {
    this.#cancelClose();
    /* Put the borrowed controls back before the section's markup is replaced,
       or they leave with the drawer. */
    for (const { node, marker } of this.#utils) {
      marker.parentElement?.insertBefore(node, marker);
      marker.remove();
    }
    this.#utils = [];
    this.#events.abort();
    if (this.dialog?.open) this.dialog.close();
    this.#unlock();
  }
}

let menu = new AgMenu();
for (const event of ['shopify:section:load', 'shopify:section:unload']) {
  document.addEventListener(event, () => {
    menu.destroy();
    menu = new AgMenu();
  });
}
